/* eslint-disable no-irregular-whitespace --
 * 本ファイルは行政書士の監修を受けた3文書の原文を保持する。条文中の全角スペースは
 * AI一覧の項目揃え（第6条）や号の字下げ（第21条）として原文が持つ体裁であり、
 * 半角化・除去は監修済み文書の改変にあたるため行わない。
 * 令和８年９月２９日の改定部分（利用規約 第４条第６項、特商法表記の住所・返品の取扱条件）は
 * 監修を受けていない（docs/terms-simple-facts.md・.aidlc/cycles/v3.7.0/inception/decisions.md DR-007 参照）。 */
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Layout from '../components/Layout';
import { Card } from '../components/ui';

interface LegalPageProps {
  type: 'terms' | 'privacy' | 'commerce';
}

interface LegalContent {
  title: string;
  intro?: string;
  sections: { title: string; content: string }[];
  date?: string;
}

/** 3文書に共通の監修者表記（表記文言は監修者の指定どおり）。 */
const SUPERVISOR = '法的事項監修　くろさわ行政書士事務所　行政書士　黒澤英樹';
const SUPERVISOR_NOTE =
  '※監修は、利用規約・プライバシーポリシー・特定商取引法に基づく表記について法的観点から確認・助言を受けたものであり、本サービスの内容や機能を保証するものではありません。';

export default function LegalPage({ type }: LegalPageProps) {
  const content: Record<LegalPageProps['type'], LegalContent> = {
    terms: {
      title: '利用規約',
      intro:
        'otomochiサービス提供者（以下「サービス提供者」といいます）が提供するサービス「otomochi」（以下「本サービス」といいます）における契約条件は、以下の利用規約（以下「本規約」という）に定めるとおりとします。本サービスをご利用いただくにあたっては、本規約の全文をお読みいただいたうえで、本規約の全ての条項について承諾いただく必要があります。本サービスをご利用いただいた場合、利用者が本規約の内容を理解しており、かつ、本規約の全ての条項について承諾したものとみなします。',
      sections: [
        {
          title: '第１条（利用者）',
          content:
            '利用者とは、本規約に同意し、次条の利用契約・利用者登録の手続きを完了した者（個人又は法人その他の団体）をいいます。',
        },
        {
          title: '第２条（利用契約）',
          content: `利用希望者がサービス提供者の定める方法によってその申し込みを行い、サービス提供者がこれを審査・承認することによって、利用者登録手続きが行えるものとします。
２．サービス提供者は、前項の審査の過程において、登録希望者が以下の各号のいずれかに該当すると判断した場合、利用者登録の申請を承認しないことがあり、その理由については一切の開示義務を負わないものとします。
⑴ 利用者登録に際して虚偽の申告をした場合
⑵ 本規約に違反したことがある者からの利用者登録が行われた場合
⑶ その他サービス提供者が利用者として不適当と判断した場合`,
        },
        {
          title: '第３条（利用料金及び支払方法）',
          content: `利用者は、本サービス利用の対価として、サービス提供者がウェブサイト上に掲載する料金表に定める利用料金を、サービス提供者が指定する方法により支払うものとします。
２．サービス提供者は、前項の料金表を変更することができるものとします。この場合、サービス提供者は、変更内容及び適用開始時期をウェブサイトへの掲載その他適切な方法により事前に周知するものとします。
３．利用者が利用料金の支払を遅滞した場合、利用者は、サービス提供者に対し年14.6%の割合による遅延損害金を支払うものとします。`,
        },
        {
          title: '第４条（有料プランの課金及び解約）',
          content: `本サービスの有料プランは、月額課金方式により提供されます。利用者が有料プランに申し込みを行った場合、初回課金日を起算日として、以後毎月同日に自動的に更新および課金が行われるものとします。
２．利用者は、マイページからいつでも有料プランの解約手続きを行うことができます。解約手続きが行われた場合であっても、既に課金済みの利用期間の満了日（次回更新日の前日）までは本サービスを利用することができます。
３．利用者が月途中で解約手続きを行った場合であっても、日割り計算による返金は行いません。
４．利用者が有料プランの変更を行った場合、変更内容に応じて次のとおり取り扱います。上位プランへの変更（アップグレード）は、変更手続きの時点で直ちに適用され、変更後のプランの月間利用枠の上限が適用されます。この場合、当該課金期間の残り期間に対応する差額を日割りで計算して請求するものとし、当該課金期間において既に使用した時間及び課金の起算日は変更しません。下位プランへの変更（ダウングレード）は、次回更新日から適用されるものとし、それまでの期間は変更前のプランのままご利用いただけます。
５．本サービスには、都度課金によるワンショット利用が含まれる場合があります。当該利用について自動更新は行われません。
６．有料プランの月間利用枠は、各課金期間の開始日において当該プランの基本枠へ初期化されるものとし、未使用の残枠を翌課金期間へ繰り越すことはありません。ただし、都度課金によるワンショット利用で購入した利用時間及び利用回数は、課金期間の区切りにかかわらず、消費されるまで保持されます。`,
        },
        {
          title: '第５条（本規約の範囲と変更）',
          content: `本規約は、利用者とサービス提供者との間の本サービスの利用に関わる一切の関係に適用されます。
２．サービス提供者は、利用者の承諾を得ることなく、サービス提供者が適当と判断する方法で利用者に通知することにより、本規約を変更できるものとします。
３．サービス提供者が本サービス又はサービス提供者のウェブサイト（以下「本サイト」といいます）上で掲載する本サービスの利用に関するルールは、本規約の一部を構成するものとします。
４．本規約の内容と、前項のルールその他の本規約外における本サービスの説明等とが矛盾・抵触する場合は、当該説明等の規定を優先させる旨の特段の定めがない限り、本規約の規定が優先して適用されるものとします。`,
        },
        {
          title: '第６条（サービスの提供）',
          content: `本サービスで使用する人工知能（ＡＩ）の種類は以下の通りです。
（１）音声認識AI
⑴ AI名称　　Faster-Whisper（OpenAI Whisperベース）
⑵ AI分類　　音声認識AI（Speech-to-Text）
⑶ 機能　　　音声を聞き取り、忠実にテキストへ変換する
⑷ 開発元　　OpenAI（米国）が開発したモデルを高速化したもの
（２）用語補正AI
⑴ AI名称　　大規模言語モデル（LLM／オープンソースの言語モデル）
⑵ AI分類　　大規模言語モデル（LLM）
⑶ 機能　　　書き起こし結果のうち、固有名詞・専門用語等の表記ゆれを、利用者が登録した用語集に基づいて整える。表記の補正のみを行い、発言内容の創作・要約・改変は行わない
⑷ 開発元　　オープンソースとして公開された言語モデルを、サービス提供者が自ら保持・運用するもの
２．前項のAIによる処理について、サービス提供者は以下を遵守します。
⑴ 利用者の音声データ及びテキストデータを、AIの学習又は再学習に利用しません。
⑵ 本サービスで使用するAIは、いずれもサービス提供者が自ら保持するモデルを、サービス提供者の管理の下で稼働させるものであり、OpenAI、Anthropicその他の第三者が提供する生成AIサービス（外部のAI API等）へ、利用者のデータを提供・送信することはありません。
⑶ AIによる処理は、本サービスの書き起こし及び補正の目的の範囲内でのみ行います。処理に用いた音声データ等の保存期間及び削除の取扱いについては、別途定めるプライバシーポリシー及び本規約の定めに従います。
⑷ 用語補正に用いる大規模言語モデル（LLM）は、用語集に基づく表記ゆれの補正に限定して利用し、新たなコンテンツの生成・創作や、発言内容の改変は行いません。
３．サービス提供者は、自らの判断により、本サービスの機能の変更及び追加などを随時実施することができることとします。`,
        },
        {
          title: '第７条（利用者登録）',
          content: `利用者は、自らの責任と費用において、ハードウェア、ソフトウェア、インターネット接続回線、セキュリティの確保等、本サービスの利用に必要な環境を整備します。
２．利用者は、いかなる場合にも、利用者ID及びパスワードを第三者に譲渡又は貸与することはできません。
３．サービス提供者は、利用者ID及びパスワード（以下「ID等」といいます。）が正しい組み合わせでログインされた場合、その利用者IDを登録している利用者によりなされたものとみなします。
４．本サービスを利用するためID等の発行を受けた場合、利用者は、第三者（本件サービスを利用する権限のない利用者の従業員を含みます。）に開示又は漏洩することがないよう善良な管理者の注意をもって管理するものとします。利用者の責めに帰すべき事由により、ID等の漏洩その他不正アクセス等により、利用者に損害が生じた場合であっても、サービス提供者は、一切の賠償責任を負わないものとします。
５．利用者は、登録情報の登録にあたって、真実かつ正確な情報を提供していただきます。サービス提供者は、利用者自身が登録した登録情報を前提として、本サービスを提供いたします。登録情報の内容に虚偽、誤り又は記載漏れがあったことにより利用者に生じた損害について、サービス提供者は一切責任を負いません。
６．未成年の方が本サービスを利用する際には、利用登録前に保護者の同意を得るものとします。
７．サービス提供者は、利用者が以下の各号のいずれかに該当する場合、当該利用者の利用者登録を拒否し、又は既に行った利用者登録を取り消すことができるものとします。なお、この場合サービス提供者は、当該利用者に対し理由を開示する義務を負わないものとします。
⑴ 当該利用者が、既に会員になっている場合。
⑵ 当該利用者の登録申請内容が、他の利用者の登録情報の全部または一部と同じである場合。
⑶ 当該利用者が、過去に利用規約に違反し会員資格の取り消し等の処分を受けたことがある場合。
⑷ 申請内容に虚偽、記入漏れ、誤記があった場合。
⑸ 未成年の利用者が、保護者の同意を得ていない場合。
⑹ クレジットカード会社、金融機関より利用停止処分が行われている場合。
⑺ その他、合理的な理由により利用者登録申請を承認することが不適当とサービス提供者が判断した場合。`,
        },
        {
          title: '第８条（プライバシー）',
          content: `サービス提供者は、ユーザー情報および個人情報を、サービス提供者が別途定めるプライバシーポリシーおよび個人情報保護法その他の法令に基づき、適切に取り扱います。
２．サービス提供者は、本サイト又は本サービス上において、利用者が送信を行った文章、その他のデータ、並びに音声データ、テキストデータ、翻訳データ、音声合成データその他本サービスの利用によって得られたデータの中に利用者以外の第三者の個人情報が含まれていた場合、サービス提供者は、当該第三者の個人情報を利用することはありません。
３．サービス提供者は、利用者が本サービスの利用にあたってサービス提供者または本サービス上に提供した個人情報について、法令で定める範囲または以下の利用目的の範囲を超えて利用することはありません。また、本サイト又は本サービス上において、利用者が本サービスを利用して送信した音声データ等について、サービス提供者は、プライバシーポリシー第６条第３項に定める保持及び自動削除の取扱いの範囲を超えて、データの内容自体を保存使用することはありません。ただし、本サービスの利用頻度、時間等のデータは除きます。
⑴ 本サービスを提供するため
⑵ 本サービスのご利用状況を調査・分析するため
⑶ 本サービスの開発、改善、メンテナンス等を行うため
⑷ 利用者を識別することのできない形式に加工した統計データを作成・利用するため
⑸ 利用者からのお問い合わせに対応するため
⑹ セキュリティ向上、不正利用の防止等、本サービスの安全・信頼を向上させる取り組みを行うため
⑺ 本サービスを円滑に提供するため
⑻ 上記の利用目的に付随する目的に利用するため`,
        },
        {
          title: '第９条（本サービスの利用方法及び利用上の注意事項）',
          content: `利用者は、サービス提供者が指定する端末・OS等の環境（以下「対応環境」といいます。）にて本サービスを利用するものとし、対応環境以外の環境では本サービスの全部又は一部が利用できない場合があること、また、サービス提供者が事前の予告なく対応環境を変更する場合があることについてあらかじめ同意するものとします。
２．本サービスは、AIを使用して音声を聞き取り忠実に変換・テキスト化するものですが、書き起こし精度について利用者の現状やニーズに完全対応するものではありません。利用者は、書き起こしにより生成されたテキストデータの内容を随時確認し、適宜編集して利用するものとします。
３．サービス利用者は、アップロードする音声ファイルについて、必要な権利を有して使用するものとし、録音に含まれる参加者からの同意を得ていることを利用の前提条件とします。また、第三者の著作権・肖像権等の諸権利及びプライバシーを侵害してはならず、その侵害行為についてサービス提供者はその責を負いません。
４．利用者が本サービスを利用するにあたって入力・提供したすべての情報に関する、所有権、知的財産権その他一切の権利（ただし本サービスを提供するための必要な範囲でサービス提供者が当該情報を利用する権利を除く）は、すべて利用者に帰属するものであり、上記情報が第三者の当該権利を侵害するものであったとしても、サービス提供者は一切の責任を負いません。
５．本サービスでは、音声データの文字起こし処理を行うため、サービス提供者が管理するクラウドサーバーを利用します。音声データの取扱い、保存及び削除の方法等の詳細については、プライバシーポリシーの定めによるものとします。`,
        },
        {
          title: '第１０条（禁止事項）',
          content: `サービス提供者は、利用者が本サービスの利用にあたり、以下の各号の行為を行うことを禁止します。
⑴ 法令、本規約又は公序良俗に違反する行為
⑵ 犯罪行為に関連する行為
⑶ サービス提供者のサーバー若しくはネットワークの機能を破壊又は妨害する行為、その他サービス提供者のサービスの運営を妨害するおそれのある行為
⑷ ウィルス等の有害なコンピューター・プログラム等を送信又は掲載する行為
⑸ 他の利用者に関する個人情報等を収集又は蓄積する行為
⑹ 他の利用者になりすます等、虚偽を用いて本サービスを利用する行為
⑺ サービス提供者のサービスに関連して、反社会的勢力に対して直接又は間接に利益を供与する行為
⑻ 著作物の無断書き起こしや、第三者の発言の無断公開、第三者の権利やプライバシーの侵害行為
⑼ 違法コンテンツのアップロード
⑽ その他サービス提供者が不適切と判断する行為`,
        },
        {
          title: '第１１条（利用制限及び登録抹消）',
          content: `サービス提供者は、利用者が以下の各号のいずれかに該当した場合、事前の通知をすることなく、利用者に対して、本サービスの全部若しくは一部の利用を制限し、又は利用者としての登録を抹消することができるものとします。
⑴ 本規約のいずれかの条項に違反した場合
⑵ 登録事項に虚偽の事実があることが判明した場合
⑶ その他サービス提供者が利用者として適当でないと判断した場合
２．サービス提供者は、本条に基づきサービス利用者が行った行為により利用者に生じた損害について、一切の責任を負いません。`,
        },
        {
          title: '第１２条（免責及び損害賠償の制限）',
          content: `本サービスに関して利用者に損害が生じた場合において、サービス提供者が責任を負うときは、サービス提供者の軽過失による場合、当該損害の直接の原因となった事由が発生した時点から遡って過去1か月間に、当該利用者がサービス提供者に対して支払った利用料金の総額を上限として、賠償責任を負うものとします。
２．前項の利用料金の支払いがない場合、サービス提供者が負う損害賠償額の上限は金500円とします。
３．前二項の規定は、サービス提供者の故意又は重過失による場合には適用されないものとし、その場合にサービス提供者は、民法その他の法令の定めに従い損害賠償責任を負うものとします。
４．サービス提供者は、本サービスに関して、利用者と他の利用者又は第三者との間において生じた損害又は紛争等について責任を負わないものとします。
５．本サービスにおけるAI（人工知能）により生成された文章は、音声ファイルをテキスト化した結果を出力するものであり、その正確性、完全性、特定の目的への適合性等についてサービス提供者が保証するものではありません。利用者はこの点を理解のうえ本サービスを利用するものとします。`,
        },
        {
          title: '第１３条（退会及びアカウント削除）',
          content: `利用者は、マイページからいつでも退会又はアカウント削除の申請を行うことができます。アカウント削除の実行にあたっては確認画面を表示し、利用者の明示的な操作により申請を受け付けるものとします。
２．利用者が有料プランを契約中にアカウント削除を申請した場合、当該申請の時点で有料プランは解約されるものとします。この場合であっても、既に支払済みの利用料金については返金を行いません。なお、利用者には、有料プランを解約し利用期間満了後に退会する方法を推奨しますが、即時退会も可能とします。
３．サービス提供者は、退会処理に伴い、利用者のアカウント情報（メールアドレス、ニックネーム、認証情報）、OAuth連携情報、利用履歴及びプラン情報等のデータを削除するものとします。なお、本サービスで利用される音声データは、文字起こし処理後に自動削除されますが、プライバシーポリシー第６条第３項に基づき保持期限が到来していない音声データ及び書き起こし結果が存在する場合には、これらも退会時の削除対象に含まれるものとします。
４．サービス提供者は、決済代行サービス（Stripe）を利用している場合、退会処理に伴い、当該決済代行事業者に保存されている顧客情報又はサブスクリプション情報についても削除手続きを行うものとします。
５．前各項に基づくデータ削除は、退会申請受付後３０日以内に完了するものとします。
６．前各項の規定にかかわらず、電子帳簿保存法、所得税法その他の法令に基づき保存義務のある取引記録については、当該法令に定める保存期間が経過するまで保持する場合があります。`,
        },
        {
          title: '第１４条（機密保持）',
          content: `利用者及びサービス提供者は、本サービスの提供に関して相手方より提供された技術上、営業上その他業務上の情報（以下、「機密情報」といいます。）について、善良なる管理者の注意をもってその機密を保持するものとし、本サービスの提供に従事する者に使用させる場合、本条第３項に規定する場合及びプライバシーポリシー第４条に規定した場合を除き、機密情報を第三者に開示してはならないものとします。
２．前項に関わらず、本サービスの提供に関して次の各号の一に該当する資料及び情報は機密情報に含まれないものとします。
⑴ 既に公知のもの又は自己の責に帰すことのできない事由により公知となったもの
⑵ 既に保有しているもの
⑶ 守秘義務を負うことなく正当に入手したもの
⑷ 相手方から開示を承諾されたもの
⑸ 機密情報によらず独自に開発し又は知り得たもの
⑹ 個別に識別が不可能な状態で統計資料としてまとめたもの
３．利用者及びサービス提供者は、相手方から受けた機密情報について、本サービスの提供及び利用の目的の範囲内に限り使用することに同意するものとします。
４．本サービスのうち、個人情報に関する取扱については、プライバシーポリシーの通りとします。
５．本条の機密保持義務は、利用契約期間終了後も有効に継続するものとします。`,
        },
        {
          title: '第１５条（通知又は連絡）',
          content: `利用者とサービス提供者との間の通知又は連絡は、本サービス上での掲示又はメール等、サービス提供者が適当と判断する方法によって行うものとします。
２．前項に規定する方法により行った通知又は連絡は、本サービス上での掲載又はメールの送信がなされた時点から効力を生じるものとします。利用者側の設定により掲載の閲覧またはメールの受信をすることができなかったとしても、サービス提供者は一切の責任を負いません。`,
        },
        {
          title: '第１６条（サービス内容の変更等）',
          content: `サービス提供者は、利用者への事前の通知なく、本サービスの内容の全部又は一部を変更し、又は提供を中止することがあります。ただし、サービス提供者は、可能な限り事前に利用者へ通知するよう努めるものとします。
２．サービス提供者は、前項に基づく本サービスの変更又は中止により利用者に生じた損害について、サービス提供者の故意又は重過失による場合を除き、責任を負わないものとします。`,
        },
        {
          title: '第１７条（本サービスの提供の停止等）',
          content: `サービス提供者は、以下の各号のいずれかの事由があると判断した場合、利用者に事前に通知することなく本サービスの全部若しくは一部の提供を停止又は中断することができるものとします。
⑴ 本サービスにかかるコンピュータシステムの保守点検又は更新を行う場合
⑵ 火災、停電又は天災等の不可抗力により、本サービスの提供が困難となった場合
⑶ コンピュータ又は通信回線等が事故により停止した場合
⑷ その他サービス提供者が本サービスの提供が困難と判断した場合
２．サービス提供者は、本サービスの提供の停止若しくは中断により、利用者若しくは第三者が被ったいかなる不利益又は損害について、一切の責任を負いません。`,
        },
        {
          title: '第１８条（本サービスの終了）',
          content: `サービス提供者は、やむを得ない事情により本サービスの全部又は一部を終了する場合には、終了予定日の６０日前までに、本サービス上への掲載、電子メールその他サービス提供者が適当と認める方法により、利用者に通知するものとします。
２．前項の通知が行われた場合、サービス提供者は、当該通知の日以降、利用者の有料プランの自動更新を停止するものとし、既に支払済みの利用期間の満了をもって当該プランは終了するものとします。
３．利用者は、本サービスの終了日までの間に、自己の責任において必要なデータの保存又はエクスポートを行うものとします。
４．サービス提供者は、本サービス終了日から３０日以内に、利用者のアカウント情報及び個人情報を削除するものとします。ただし、法令に基づき保存が義務付けられている情報については、この限りではありません。`,
        },
        {
          title: '第１９条（設備の負担等）',
          content: `本サービスの提供を受けるために必要な、コンピューター、スマートフォンその他の機器、通信回線その他の通信環境等の準備及び維持は、利用者の費用と責任において行うものとします。
２．利用者は自己の本サービスの利用環境に応じて、コンピューター・ウィルスの感染の防止、不正アクセス及び情報漏洩の防止等のセキュリティ対策を自らの費用と責任において講じるものとします。サービス提供者は、利用者による本サービスの利用環境、セキュリティ対策について一切関与せず、一切の責任を負いません。`,
        },
        {
          title: '第２０条（権利帰属）',
          content: `本サイト及び本サービスに関する所有権及び知的財産権は全てサービス提供者又はサービス提供者にライセンスを許諾している者に帰属しており、本規約の定めに基づく本サービスの利用許諾は、本規約において明示されているものを除き、当該知的財産権の譲渡又は利用許諾を意味するものではありません。
２．第９条４項記載のとおり、音声ファイル等の入力・提供したすべての情報に関する、所有権、知的財産権その他一切の権利は、すべて利用者に帰属します。また、書き起こし結果についても利用者に諸権利が帰属し、サービス提供者はその権利を主張しません。
３．本サービスで使用するAIモデルに関する所有権及び知的財産権は開発者に帰属し、サービス提供者は開発者より利用許諾を得て使用しています。`,
        },
        {
          title: '第２１条（反社会的勢力の利用の拒絶）',
          content: `サービス提供者は、利用者が次の事由に該当すると認められる場合、催告その他の手続きを要することなく、本サービスの利用をお断りします。
⑴ 暴力団、暴力団員、暴力団員でなくなった時から５年を経過しない者、暴力団準構成員、暴力団関係企業、総会屋等、社会運動等標ぼうゴロまたは特殊知能暴力集団等、その他これらに準ずる者（以下、「暴力団員等」といいます。）に該当すること
⑵ 暴力団員等が経営を支配していると認められる関係を有すること
⑶ 暴力団員等が経営に実質的に関与していると認められる関係を有すること
⑷ 利用者もしくは第三者の不正の利益を図る目的または第三者に損害を加える目的をもってするなど、不当に暴力団員等を利用していると認められる関係を有すること
⑸ 暴力団員等に対して資金等を提供し、または便宜を供与するなどの関与をしていると認められる関係を有すること
⑹ 役員または経営に実質的に関与している者が暴力団員等と社会的に非難されるべき関係を有すること
⑺ 利用者自らまたは第三者を利用して次のいずれかに該当する行為を行うこと
　① 暴力的な要求行為
　② 法的な責任を超えた不当な要求行為
　③ 本サービスの利用に関して、脅迫的な言動をし、または暴力を用いる行為
　④ 風説を流布し、偽計を用いまたは威力を用いて信用を毀損し、または業務を妨害する行為
　⑤ その他前各行為に準ずる行為
２．前項の規定に基づき本サービスが解除された場合、サービス提供者は当該解除を理由とするいかなる損害等についても賠償等の責任を一切負わないものとします。`,
        },
        {
          title: '第２２条（準拠法及び合意管轄）',
          content: `本規約に関する準拠法は、日本法とします。
２．本規約及び本サービスに関する一切の紛争については、サービス提供者の住所地を管轄する裁判所を第一審の専属的合意管轄裁判所とします。

以上`,
        },
      ],
      date: '制定日　令和８年４月１日\n改定日　令和８年９月２９日（第４条第６項を変更）',
    },
    privacy: {
      title: 'プライバシーポリシー',
      intro:
        'otomochiサービス提供者（以下「サービス提供者」といいます）は、サービス提供者が提供するサービス「otomochi」（以下「本サービス」といいます）において、サービス提供者が取得する個人情報等を適切に管理運用するために遵守するべき基本的事項として、以下の通りプライバシーポリシー（以下「本ポリシー」といいます）を定め、個人情報等を適正に取り扱います。なお、本ポリシーにおける用語の定義は、本サービスに係る利用規約等に準じるものとします。',
      sections: [
        {
          title: '第１条（プライバシーポリシーへの同意）',
          content: `利用者は、本サービスの会員登録を行うにあたり、本ポリシーの内容を確認した上で、登録画面においてチェックボックスにチェックを入れる方法により、本ポリシーに同意するものとします。
２．利用者が前項の同意のうえ会員登録を行い、本サービスを利用した場合、または提供サイトの閲覧を継続した場合には、本ポリシーに同意したものとします。`,
        },
        {
          title: '第２条（個人情報等の範囲）',
          content: `本ポリシーにおいて、「個人情報等」とは、サービス提供者が本サービスを通して取得する以下の情報をいいます。サービス提供者は、個人情報等の取得に際しては、あらかじめ取得目的を正当な事業の範囲内で明確に定め、その目的達成に必要な限度において、適法かつ公正な方法で取得し、利用します。
⑴ 利用者が本サービス利用のため、所定のフォーム等に必要事項を入力・登録した利用者ID（メールアドレス）、ニックネーム等のアカウント作成に必要な情報
⑵ Google、Discord等の他サービスとの連携機能（OAuth連携）を通じて提供された連携サービスのアカウント情報
⑶ クレジットカード情報等の決済手段に関する情報。本サービスでは、決済代行サービス（Stripe）を利用しています。クレジットカード情報等の決済情報は、当該決済代行事業者が取得・管理するものであり、サービス提供者は利用者のクレジットカード情報を直接取得または保存することはありません。
⑷ 書面、メール、電話その他の手段により、お問い合わせ、ご意見、ご要望等をご提供いただく場合、又は苦情その他のユーザーからのフィードバック情報等
⑸ 本サービスを利用する際に使用したデバイスのOS、アプリケーションのUID（固有識別子）及びバージョン情報
⑹ 本サービスの利用状況に関する情報（動作設定の情報、ファイルのアップロード数、文字起こし時間等）
⑺ 本サービスの操作等に関する情報
⑻ 本サービスへのアクセス状況やそのご利用方法に関する情報`,
        },
        {
          title: '第３条（個人情報等の利用目的）',
          content: `サービス提供者は以下の内容で個人情報等を利用することとし、取り扱う個人情報等は、事業の内容及び規模を考慮し、利用目的の範囲に限定して、適切に取得・利用・提供し、目的外利用をいたしません。また、目的外利用を行わないための措置を講じます。
⑴ 本サービスの提供・運営
⑵ 登録確認、本人認証、会員管理、サービス提供および利用料金の請求・決済手続きの実施
⑶ 本サービスに関する各種お問い合わせやサポート対応
⑷ 本サービスの改善・新規開発およびマーケティング
⑸ 利用状況、アクセスログ等のデータを分析し、本サービスの機能向上や新規サービスの開発、キャンペーンの実施、各種ご案内の提供
⑹ セキュリティの確保および事故・事件への対応
⑺ 不正利用の防止、サービスの安全性確保、事故や事件発生時の調査およびご連絡
２．サービス提供者は、変更前の利用目的と関連性を有すると合理的に認められる範囲内において個人情報等の利用目的を変更することがありますが、利用目的を変更した場合には速やかに通知又は公表します。`,
        },
        {
          title: '第４条（個人情報等の第三者への提供）',
          content: `サービス提供者は、原則として利用者の個人情報等を第三者に提供いたしません。ただし、以下の場合はその例外とします。
⑴ 国の機関、地方公共団体、またはその委託を受けた者が法令に基づき事務を遂行する場合
⑵ 人の生命、身体、または財産の保護のために必要な場合（本人の同意を得ることが困難な場合）
⑶ 公衆衛生の向上や児童・青少年の健全な育成の推進のために必要な場合
⑷ 利用者が本ポリシーに違反し、サービス提供者もしくは他の利用者の権利および財産、本サービスまたは本サイトを保護するために、公開せざるをえないと判断した場合
⑸ 個人情報の保護に関する法律（個人情報保護法）その他の法令で認められる場合`,
        },
        {
          title: '第５条（個人情報の開示、訂正、利用停止等）',
          content: `利用者は、サービス提供者が保有する利用者の個人情報等について、開示、訂正、追加、削除、利用停止、消去（以下「開示等」といいます。）を請求することができます。ただし、原則として、開示等の対象は個人情報の保護に関する法律（個人情報保護法）上の個人情報に該当する情報にとどまり、すべての利用者情報が対象となるものではありません。
２．当サービス提供者は、利用者本人より、個人情報等の訂正、利用停止、削除の依頼があった場合、本人確認の上で遅滞なく対応します。なお、依頼内容が、法令に照らして理由がないものとサービス提供者が認めた場合、不合理に繰り返される要請である場合等、サービス提供者が不当な要請であると判断する要請については拒否・却下することがあります。`,
        },
        {
          title: '第６条（通信の秘密　個人情報等の保護）',
          content: `サービス提供者は、通信の秘密を尊重し、法令に基づく場合を除き、利用者が本サービスを利用して送信した音声データその他の情報について、法令に基づく場合を除き、第三者に開示又は提供しません。
２．利用者が本サービスを利用して送信する音声データには、声紋その他の情報により特定の個人を識別することができる場合があり、個人情報に該当する可能性があります。また、TRPGセッション等の会話内容には、健康状態、思想信条その他の要配慮個人情報に該当する情報が含まれる可能性があります。利用者は、これらの可能性を理解したうえで、本サービスを利用するものとします。
３．サービス提供者は、利用者が送信した音声データについて、原則としてクラウド上に保存せず、文字起こし処理の結果を提供した後は自動的に削除するものとします。書き起こし結果（テキストデータ）は、文字起こし処理の完了時を起算点として、無制限プランの利用者については７日間、その他のプランの利用者については８時間、サーバ上に保持し、当該保持期限の到来時に自動的に削除するものとします。ただし、無制限プランの利用者については、結果画面での音声の聞き直し（セグメント単位の再生）を提供するため、処理済みの音声データを書き起こし結果と同じ保持期限（完了から7日間）までサーバ上に保持し、当該利用者本人のみがアクセスできるよう管理します。当該保持期限の到来時には、音声データは書き起こし結果とともに自動的に削除されます。また、本サービスの利用頻度、利用時間その他の利用状況に関する情報については、この限りではありません。
４．サービス提供者は、個人情報等の漏えい、滅失又は毀損の防止その他個人情報等の保護のため、個人情報ファイルへのアクセス制限、アクセスログの記録及び外部からの不正アクセス防止のためのセキュリティ対策等、必要かつ適切な安全管理措置を講じます。
５．本サービスでは、音声データの処理および本サービスの運営のため、クラウドインフラサービスを利用しています。当該クラウドインフラ事業者は、サービス提供者による個人情報の取扱いの委託先として位置付けられます。
６．サービス提供者は、個人情報保護法その他の法令に従い、委託先に対して必要かつ適切な監督を行います。
７．クラウドインフラの利用に伴い、個人情報等が外国に所在するサーバーにおいて処理される場合があります。この場合、サービス提供者は個人情報保護法その他の関係法令に従い、適切な安全管理措置を講じます。`,
        },
        {
          title: '第７条（Cookieの利用）',
          content:
            'サービス提供者は、本サービスの利便性を高めるため、本サービスを利用する際に、クッキー（Cookie）を利用して利用者のアクセスデータを自動的に収集しています。クッキーは、サーバーから送信して利用者のコンピュータに小さなデータファイルとして保存されるウェブサイトのアクセス履歴などの情報で、利用者を確認するための仕組みです。クッキーには氏名、住所など利用者個人を特定する情報は含んでいません。利用者は各ブラウザの「設定」からCookieを削除することができますが、利用者がCookieを削除することによって一部の機能に影響が出る可能性があることについて、利用者はこれを了承するものとします。',
        },
        {
          title: '第８条（プライバシーポリシーの範囲）',
          content:
            '本ポリシーは、サービス提供者の業務提携先企業・個人、業務委託先企業・個人、サービス提供者の運営するウェブサイト内のリンク先のサイト等には適用されません。それらのサイト等においてトラブル又は紛争等が発生した場合は、サービス提供者は一切責任を負いません。各サイトの個人情報等の取扱いについてご確認下さい。',
        },
        {
          title: '第９条（プライバシーポリシーの改定）',
          content:
            'サービス提供者は、法令の改正や本サービスの運用状況に応じて、本ポリシーを随時見直し、変更することがあります。変更があった場合は、ウェブサイト上に掲載し、必要に応じて利用者に通知することもあります。変更後のプライバシーポリシーは、ウェブサイト掲載日以降の利用に適用されるものとします。',
        },
        {
          title: '第１０条（お問い合わせ窓口）',
          content: `個人情報等の取扱いに関するご質問や苦情等のお問い合わせについては、下記の窓口までご連絡ください。

メールアドレス：support@hoshikusaya.com`,
        },
      ],
      date: '制定日　令和８年４月１日\n改定日　令和８年９月１日（お問い合わせ窓口のメールアドレスを変更）',
    },
    commerce: {
      title: '特定商取引法に基づく表記',
      sections: [
        {
          title: '販売事業者名',
          content: '干し草屋',
        },
        {
          title: '事業者氏名',
          content: '※ご請求時には販売者情報を遅滞なく開示いたします。',
        },
        {
          title: '住所',
          content:
            '〒810-0001\n福岡県福岡市中央区天神4-6-28 いちご天神ノースビル7階',
        },
        {
          title: '電話番号',
          content: '※ご請求時には販売者情報を遅滞なく開示いたします。',
        },
        {
          title: 'お問い合わせメールアドレス',
          content: 'support@hoshikusaya.com',
        },
        {
          title: '販売価格帯',
          content: 'サービスごとに販売価格（消費税等含む）を設定し、該当ページで表示します。',
        },
        {
          title: '商品等の引き渡し時期（日数）・発送方法',
          content: 'オンラインにおけるサービス利用',
        },
        {
          title: '代金の支払時期および方法',
          content: 'サービスごとに支払時期および方法を指定し、該当ページで表示します。',
        },
        {
          title: 'お申込み期間',
          content: 'お申込み期間の制限がある場合は、各商品ページ等に表示されます。',
        },
        {
          title: '商品代金以外に必要な費用／送料、消費税等',
          content: 'なし',
        },
        {
          title: '返品の取扱条件／返品期限、返品時の送料負担または解約や退会条件',
          content:
            '解約は、利用者がマイページから行うサービス提供者所定の手続きにより可能です。解約手続きを行った場合であっても、既に課金済みの利用期間の満了日までは本サービスをご利用いただけます。但し、既に支払われた利用料金についての返金は行いません。月途中で解約した場合であっても日割り計算による返金は行いません。尚、本サービスは通信販売に該当するため、特定商取引法に基づくクーリング・オフ制度の適用対象外となります。都度課金によるワンショット利用（利用時間又は利用回数の購入）については、自動更新は行われず、購入手続きの完了後のキャンセル及び返金には応じられません。ただし、購入した利用時間又は利用回数が付与されなかった場合は、お問い合わせにより返金いたします。また、不良品の取扱条件に定める場合は、同条件に従います。',
        },
        {
          title: '不良品の取扱条件',
          content:
            '本サービスはデジタルサービスの性質上、返品又は交換には応じられません。ただし、本サービスが正常に利用できない等、サービス提供者の責に帰すべき重大な不具合が確認された場合には、状況を確認のうえ必要に応じて個別に対応いたします。',
        },
      ],
      date: '制定日　令和８年４月１日\n改定日　令和８年９月１日（販売事業者名およびお問い合わせメールアドレスを変更）\n改定日　令和８年９月２９日（住所を変更し、ワンショット利用の返品の取扱条件を追記）',
    },
  };

  const pageContent = content[type];

  return (
    <Layout>
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="inline-flex items-center text-primary hover:text-primary-hover">
            <ArrowLeft className="w-5 h-5 mr-2" />
            トップに戻る
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <Card padding="none" className="p-8 md:p-12">
          <h1 className="text-3xl font-bold text-text-heading mb-8">{pageContent.title}</h1>

          {/* 条文を読み慣れていない利用者への相互リンク。特商法表記は本ページの説明対象に
              含まれるものの、条文量が少なく読み下しの負担が小さいため導線は置かない。 */}
          {(type === 'terms' || type === 'privacy') && (
            <div className="mb-10 rounded-panel border border-primary/30 bg-primary/5 px-5 py-4 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-sm text-text">条文が読みづらい方へ：</span>
              <Link
                to="/terms-simple"
                className="text-sm font-medium text-primary hover:text-primary-hover underline underline-offset-2"
              >
                かんたんな説明を読む
              </Link>
              <span className="text-sm text-text-sub w-full sm:w-auto">
                （要点を平易な言葉でまとめた補助資料です。効力を持つのは本ページの本文です）
              </span>
            </div>
          )}

          {pageContent.intro && (
            <p className="text-text whitespace-pre-line leading-relaxed mb-10">{pageContent.intro}</p>
          )}

          <div className="space-y-8">
            {pageContent.sections.map((section) => (
              <div key={section.title}>
                <h2 className="text-xl font-semibold text-primary mb-3">{section.title}</h2>
                <p className="text-text whitespace-pre-line leading-relaxed">{section.content}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200 text-sm text-text-sub space-y-2">
            {pageContent.date && <p className="whitespace-pre-line">{pageContent.date}</p>}
            <p>{SUPERVISOR}</p>
            <p className="text-xs">{SUPERVISOR_NOTE}</p>
          </div>
        </Card>
      </main>
    </Layout>
  );
}
